import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import CustomSidebar from './CustomSidebar';
import { getApiAxiosInstance } from '../../../../../utils/network';
import Img from '../../../../../components/Img';
import AvatarV2 from '../../../../../components/cards/PlayerCard/ListViewComponents/AvatarV2';
import { Description } from '../../../../../components/text';
import Icon from '../../../../../components/Img/Icon';
import { refreshEventParticipantData } from '../../../../../actions/event-profile-actions';

import styles from './SwitchDivisionFlow.scss';

const SwitchDivisionFlow = ({ memberIds, fromDivisionId, onClose }) => {
    const [step, setStep] = useState(1);
    const [search, setSearch] = useState('');
    const [selectedDivisionId, setSelectedDivisionId] = useState(null);
    const [divisions, setDivisions] = useState([]);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const { event, players } = useSelector(({ eventProfile }) => {
        const { event, players } = eventProfile;
        return { event, players };
    });

    const dispatch = useDispatch();

    const eventDivisions = event.data?.eventDivisions || [];

    const filteredDivisions = divisions.filter(division => !search || division?.name.toLowerCase().includes(search.toLowerCase()));

    const fromDivision = eventDivisions.find(d => d.id === fromDivisionId);
    const selectedDivision = divisions.find(d => d.id === selectedDivisionId);
    const playersToMove = players.data?.filter(player => memberIds.includes(player.memberId)) || [];

    const seen = new Set();
    const switchMemberFromDivisions = [];

    // populate switchMemberFromDivisions request payload
    playersToMove.forEach(player => {
        if (seen.has(player.memberId)) {
            return;
        }

        const playerDivision = player.divisions?.find(d => d.id === fromDivisionId);
        const partnerId = playerDivision?.partners?.[0];

        if (partnerId && memberIds.includes(partnerId) && !seen.has(partnerId)) {
            switchMemberFromDivisions.push({
                fromDivisionId,
                memberId1: player.memberId,
                memberId2: partnerId,
            });
            seen.add(player.memberId);
            seen.add(partnerId);
        } else {
            switchMemberFromDivisions.push({
                fromDivisionId,
                memberId1: player.memberId,
            });
            seen.add(player.memberId);
        }
    });

    const fetchDivisions = async () => {
        setIsLoading(true);
        const api = getApiAxiosInstance();

        try {
            const response = await api.post(`v2/tms/events/${event.data?.id}/divisions/availableforswitching`, {
                switchMemberFromDivisions,
            });

            setDivisions(response.data?.divisionsAvailableForSwitch);
            setIsLoading(false);
        } catch (error) {
            setError(error);
            setIsLoading(false);
        }
    };

    const submitSwitch = async () => {
        setIsLoading(true);
        const api = getApiAxiosInstance();

        try {
            await api.put(`v2/tms/events/${event.data?.id}/divisions/switch`, {
                targetDivisionId: selectedDivisionId,
                switchMemberFromDivisions,
            });

            setSuccess(true);
            dispatch(refreshEventParticipantData(event.data?.id, {
                divisionIds: [fromDivisionId, selectedDivisionId].filter(Boolean),
            }));
            setIsLoading(false);
        } catch (error) {
            setError(error);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDivisions();
    }, []);

    const onConfirmClick = () => {
        if (success) {
            onClose();
        } else {
            submitSwitch();
        }
    };

    const renderStep2StatusTitle = () => {
        if (success) {
            return <div className={`${styles.stepTitle} ${styles.success}`} data-testid="event-profile.switch-division.success-title"><Icon size={20} src={'/img/v4/circle-check.svg'} /> Success!</div>;
        }
        if (error) {
            return (
                <>
                    <div className={`${styles.stepTitle} ${styles.error}`} data-testid="event-profile.switch-division.error-title"><Icon size={20} src={'/img/v4/triangle-exclamation.svg'} /> Error!</div>
                    <div className={styles.error} data-testid="event-profile.switch-division.error-msg">{error.message}</div>
                </>
            );
        }
        return <div className={styles.stepTitle} data-testid="event-profile.switch-division.confirm-title">Confirm Switch</div>;
    };

    return (
        <CustomSidebar
            sidebarOpen={true}
            title="Switch Division"
            subtitle={event.data?.name}
            handleOverlayClick={onClose}
            showSplash={isLoading}
            handleBackClick={step === 2 && !success ? () => setStep(1) : null}
            data-testid="event-profile.switch-division"
        >
            {step === 1 && (
                <div className={styles.stepContainer}>
                    <div className={styles.stepTitle} data-testid="event-profile.switch-division.title">Select a Division to Switch Player(s) to:</div>
                    {!divisions.length ? (
                        <div className={styles.noDivisions} data-testid="event-profile.switch-division.no-divisions-wrapper">
                            <div className={styles.noDivisionsIcon}>
                                <Img src={'/img/v4/triangle-exclamation-red.svg'} />
                            </div>
                            <div className={styles.noDivisionsText} data-testid="event-profile.switch-division.no-divisions-text">
                                There are no available divisions for the player(s) to be switched to. Division switches are only allowed between divisions with the same cost, gender, and game type settings.
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className={styles.description}>
                                <ul>
                                    <li>Division switches are only allowed between divisions with the same cost, gender, and game type settings. Eligible divisions for switching are listed below.</li>
                                    <li>Once players or team divisions are switched, they will receive a notification via email about the change.</li>
                                </ul>
                            </div>
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder="Find a division"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                data-testid="event-profile.switch-division.search-input"
                            />
                            <div className={styles.divisionList}>
                                {filteredDivisions?.length ? filteredDivisions.map(division => {
                                    const isChecked = selectedDivisionId === division.id;
                                    return (
                                        <label key={division.id} className={styles.divisionOption} data-testid="event-profiles.event-profile-page-v3.label-0-label">
                                            <input
                                                type="radio"
                                                name="division"
                                                value={division.id}
                                                checked={isChecked}
                                                onChange={() => setSelectedDivisionId(division.id)}
                                                className={styles.radioInput}
                                                data-testid={`event-profile.switch-division.division-option-${division.id}`}
                                            />
                                            <span className={styles.radioIcon}>
                                                {isChecked ? <Img src={'/img/v4/radio-selected.svg'} /> : <Img src={'/img/v4/radio-empty.svg'} />}
                                            </span>
                                            <span>{division.name}</span>
                                        </label>
                                    );
                                }) : (
                                    <div className={`${styles.description} text-center`} data-testid="event-profile.switch-division.no-matches-msg">There are no matching divisions.</div>
                                )}
                            </div>
                        </>
                    )}
                    <button
                        type="button"
                        className={'btn btn-primary btn-large mt-auto text-uppercase'}
                        disabled={!selectedDivisionId}
                        onClick={() => setStep(2)}
                        data-testid="event-profile.switch-division.next-btn"
                    >
                        Next
                    </button>
                </div>
            )}

            {step === 2 && (
                <div className={styles.stepContainer}>
                    {renderStep2StatusTitle()}
                    <div className={styles.playerList}>
                        {playersToMove.map(player => (
                            <div className={styles.playerCard} key={player.memberId}>
                                <AvatarV2
                                    playerImages={player.playerProfileImages}
                                    player={player}
                                    className={styles.avatar}
                                />
                                <div className={styles.playerDetails}>
                                    <div className={styles.name}>{player.firstName} {player.lastName}</div>
                                    <div className={styles.description}>
                                        <Description desc={[player.gender, player.location?.display]} />
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div className={styles.divisionTitle} data-testid="event-profile.switch-division.from-label">From:</div>
                        <div className={styles.divisionName} data-testid="event-profile.switch-division.from-division-name">{fromDivision?.name}</div>
                        <div className={styles.divisionTitle} data-testid="event-profile.switch-division.to-label">To:</div>
                        <div className={styles.divisionName} data-testid="event-profile.switch-division.to-division-name">{selectedDivision?.name}</div>
                    </div>
                    <button
                        type="button"
                        className={'btn btn-primary btn-large mt-auto text-uppercase'}
                        onClick={onConfirmClick}
                        data-testid="event-profile.switch-division.confirm-btn"
                    >
                        {success ? 'Close' : 'Confirm Switch'}
                    </button>
                </div>
            )}
        </CustomSidebar>
    );
};

SwitchDivisionFlow.propTypes = {
  memberIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  fromDivisionId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SwitchDivisionFlow;
